import type { ILoanSchedule } from "@/modules/loan/common/types";
import { CURRENCY, DATE_FORMAT } from "@/shared/common/constants";
import { CloudflareAsset } from "@/shared/common/enums";
import { CloudFlareImage } from "@/shared/components";
import { formatDate } from "@/shared/helpers";
import type { ICellProps } from "@ab.uitools/ui-library/components/AdvancedTable/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Tooltip } from "@ab.uitools/ui-library/components/Tooltip";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";

interface Props {
  t: (s: string) => string;
}

export const getColumns = ({ t }: Props) => ([
  {
    id: "paymentDate",
    header: t("loans.scheduleTable.paymentDate"),
    accessorKey: "paymentDate",
    enableHiding: false,
    cell: ({ row }: ICellProps<ILoanSchedule>) => {
      const date = formatDate(row.original?.paymentDate, DATE_FORMAT.DEFAULT);
      const isOverdue = row.original?.isOverdue;
      return (
        <div className="flexbox align-items--center">
          <Text className="mr-6">{date}</Text>

          <Tooltip
            id={`overdue-${row.id}`}
            position={Positions.TOP_CENTER}
            text={t("loans.scheduleTable.overdue")}
          />

          {isOverdue && (
            <CloudFlareImage
              id={`overdue-${row.id}`}
              className="loan-schedule-warning-icon"
              path={CloudflareAsset.Warning}
            />
          )}
        </div>
      );
    }
  },
  {
    id: "principalAmount",
    header: t("loans.scheduleTable.principalAmount"),
    accessorKey: "principalAmount",
    enableHiding: false,
    cell: ({ row }: ICellProps<ILoanSchedule>) => {
      const amount = row.original?.principalAmount;
      const currency = row.original?.currency;
      return `${amount} ${CURRENCY[currency]?.symbol}`;
    },
  },
  {
    id: "interestAmount",
    header: t("loans.scheduleTable.interestAmount"),
    accessorKey: "interestAmount",
    enableHiding: false,
    cell: ({ row }: ICellProps<ILoanSchedule>) => {
      const interestAmount = row.original?.interestAmount;
      const currency = row.original?.currency;
      return `${interestAmount} ${CURRENCY[currency]?.symbol}`;
    },
  },
  {
    id: "totalPaymentAmount",
    header: t("loans.scheduleTable.totalPaymentAmount"),
    accessorKey: "totalPaymentAmount",
    enableHiding: false,
    cell: ({ row }: ICellProps<ILoanSchedule>) => {
      const totalAmount = row.original?.totalPaymentAmount;
      const currency = row.original?.currency;
      return `${totalAmount} ${CURRENCY[currency]?.symbol}`;
    },
  },
]);
